import { useRef, useState } from "react";
import { Check, FileText, Loader2, Send, Trash2, UploadCloud } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { GhostButton, Notice, PrimaryButton, Section, StepHeader } from "./fields";
import { SharedDetailsFields } from "./SharedDetailsFields";
import { deadlineBlocks } from "./DeadlineNote";

const ACCEPT = ".pdf,.docx,.doc";
const NO_FILE_MSG = "Your browser did not hand the file over. This happens when a file is dragged straight out of Outlook, Teams or a web page. Please save it to your computer first, then click the box to choose it.";
const BAD_TYPE_MSG = "Please upload a PDF or Word document (.pdf, .docx or .doc).";

// Drag and drop from Outlook or a web page gives no File object; pick the
// first real file from either list and say so when there is none.
const droppedFile = (dt) => {
  const fromFiles = dt?.files?.[0];
  if (fromFiles) return fromFiles;
  const items = Array.from(dt?.items || []);
  const fileItem = items.find((it) => it.kind === "file");
  return fileItem ? fileItem.getAsFile() : null;
};

function DropZone({ drafts, onUpload, onRemove, uploading, uploadError }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);
  const [localError, setLocalError] = useState(null);
  const pick = (file, fromDrop = false) => {
    setLocalError(null);
    if (!file) {
      if (fromDrop) setLocalError(NO_FILE_MSG);
      return;
    }
    if (!/\.(pdf|docx|doc)$/i.test(file.name || "")) {
      setLocalError(BAD_TYPE_MSG);
      return;
    }
    onUpload(file);
  };
  const shownError = uploadError || localError;
  return (
    <div className="flex flex-col gap-3">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        data-testid="cp-draft-input"
        onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); pick(droppedFile(e.dataTransfer), true); }}
        disabled={uploading}
        data-testid="cp-draft-upload"
        className={`group flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-[border-color,background-color] disabled:opacity-60 ${
          over ? "border-[#1B4F8A] bg-[#1B4F8A]/5" : "border-stone-300 bg-stone-50/60 hover:border-[#1B4F8A] hover:bg-white"
        }`}
      >
        <span className="w-12 h-12 rounded-full bg-white border border-stone-200 text-[#1B4F8A] flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UploadCloud className="w-5 h-5" />}
        </span>
        <span className="text-[15px] font-medium text-stone-800 mt-1">{uploading ? "Uploading your draft" : "Drop the draft contract here, or click to choose a file"}</span>
        <span className="text-xs text-stone-500">PDF or Word, up to 25 MB. You can add more than one file.</span>
      </button>
      {shownError && <Notice tone="danger" testid="cp-upload-error">{shownError}</Notice>}
      {drafts.length > 0 && (
        <ul className="flex flex-col gap-2" data-testid="cp-draft-list">
          {drafts.map((d) => (
            <li key={d.id} className="flex items-center gap-3 rounded-lg border border-stone-200 bg-white px-4 py-3 text-sm" data-testid={`cp-draft-${d.id}`}>
              <span className="w-8 h-8 rounded-md bg-[#1B4F8A]/8 text-[#1B4F8A] flex items-center justify-center shrink-0"><FileText className="w-4 h-4" /></span>
              <span className="flex-1 truncate text-stone-800 font-medium">{d.filename}</span>
              <span className="font-mono text-xs text-stone-500">{Math.max(1, Math.round(d.size_bytes / 1024))} KB</span>
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><Check className="w-3.5 h-3.5" /> Received</span>
              <button type="button" onClick={() => onRemove(d.id)} className="text-stone-400 hover:text-rose-600 transition-colors" aria-label="Remove this file" data-testid={`cp-draft-remove-${d.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Route B: the client contracts on their own paper. What Medicys needs
// to know about the incoming contract, then the shared details.
export function ClientPaperStep({ state, answers, set, onSubmit, onBack, onUpload, onRemove, busy, error, uploading, uploadError, invalid = new Set(), checking, onDeadlineCheck }) {
  const [touched, setTouched] = useState(false);
  const drafts = state.client_paper?.drafts || [];
  const a = answers;
  const draftMissing = (touched || invalid.has("draft")) && drafts.length === 0 && !a.will_follow_by_email;
  return (
    <div className="flex flex-col gap-8" data-testid="commission-client-paper">
      <StepHeader
        eyebrow="Step 3 of 5 · Your contract"
        title="Set up the project with your paperwork"
        intro="Tell us who the contract is with, where invoices should go and who to keep informed, then upload your draft contract or email it to us. Medicys reviews the draft against the proposal so the project is not held up. Your answers are saved as you type."
        aside={<GhostButton onClick={onBack} disabled={busy} testid="commission-back-to-routes">Change route</GhostButton>}
      />

      {state.stage === "client_paper_failed" && (
        <Notice tone="danger" title="We could not complete your submission last time" testid="commission-cp-failed">
          Nothing has been lost. Please check the details below and send them again. The Medicys team has been told about the problem.
        </Notice>
      )}

      <SharedDetailsFields
        a={answers} set={set} forSigning={false} startAt={1} onFile={state.client_record} invalid={invalid}
        deadline={state.deadline} onDeadlineCheck={onDeadlineCheck} checking={checking} legalEntity
      />

      {!deadlineBlocks(answers, state.deadline) && (
      <Section n="04" title="The draft contract" intro="Upload it now if you have it. Medicys reviews every draft against the proposal and comes back with comments, usually within two working days." tone="signing" testid="commission-draft">
        {onBack && (
          <p className="text-sm text-stone-500" data-testid="cp-switch-route">
            Changed your mind? You can{" "}
            <button type="button" onClick={onBack} disabled={busy} className="font-semibold text-[#1B4F8A] underline underline-offset-2 hover:text-[#143E72] disabled:opacity-50" data-testid="cp-switch-route-btn">sign the Medicys Statement of Work instead</button>
            {" "}at any time before you submit; your details are kept.
          </p>
        )}
        <DropZone drafts={drafts} onUpload={onUpload} onRemove={onRemove} uploading={uploading} uploadError={uploadError} />
        {drafts.length === 0 && (
        <label className="flex items-start gap-3.5 rounded-lg bg-white border border-stone-200 p-4 text-[15px] text-stone-800 cursor-pointer has-[[data-state=checked]]:border-[#1B4F8A]">
          <Checkbox checked={!!a.will_follow_by_email} onCheckedChange={(v) => set("will_follow_by_email", !!v)} data-testid="cp-follow-by-email" className="mt-0.5 h-5 w-5 border-stone-400 data-[state=checked]:bg-[#1B4F8A] data-[state=checked]:border-[#1B4F8A]" />
          <span>I do not have the draft yet. It will follow by email.</span>
        </label>
        )}
        {draftMissing && <Notice tone="warning" testid="cp-draft-required">Please upload the draft, or tick the box to say it will follow by email.</Notice>}
        {error && <Notice tone="danger" testid="commission-error">{error}</Notice>}
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <PrimaryButton onClick={() => { setTouched(true); onSubmit(); }} disabled={busy || uploading} testid="cp-submit" size="lg">
            {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending</>) : (<><Send className="w-4 h-4" /> Send these details to Medicys</>)}
          </PrimaryButton>
          <p className="text-sm text-stone-500">Your Medicys contact is notified straight away.</p>
        </div>
      </Section>
      )}
    </div>
  );
}
